// src/requests/requests.client.ts — PORTAL CETĂȚEAN (#/solicitari), IT-PORTAL-57.
// Cetățeanul depune o solicitare către o primărie și o urmărește: listă „Solicitările mele",
// formular de depunere, și detaliu cu thread de mesaje + atașamente + status/termen.
// Sub-navigarea e prin query (?new=1 / ?id=<uuid>) — routerul re-montează la schimbarea hash-ului.
// Operatorii primăriei (editor+ pe org-ul solicitării) văd în detaliu și controalele de preluare/status.

import "../styles.css"
import van from "vanjs-core"
import { get_hash_query_params, navigate } from "../lib/router.client"
import { session } from "../lib/session.client"
import { api_get, api_post, api_upload } from "../lib/api.client"
import type { Regim } from "../regim"

const t = van.tags

// ─────────────────────────────────────────────
// Tipuri (oglindesc API-ul din src/requests.ts)
// ─────────────────────────────────────────────
interface RequestAttachment {
	id: string
	message_id: string | null
	filename: string
	content_type: string | null
	size_bytes: number
	created_at: string
}
interface RequestMessage {
	id: string
	author_role: "citizen" | "operator" | "system"
	author_name: string
	body: string
	created_at: string
	attachments: RequestAttachment[]
}
interface RequestSummary {
	id: string
	org_id: string
	org_slug: string
	org_name: string
	citizen_name: string
	applicant_name: string // ghișeu: solicitantul real (gol pe fluxul online)
	applicant_address: string
	subject: string
	category: string
	tip_act: RequestTipAct
	tip_act_label: string // eticheta RO gata făcută de server (UI-ul NU re-mapează coduri)
	status: RequestStatus
	next_statuses: RequestStatus[] // tranzițiile LEGALE din starea curentă (deja filtrate pe tip_act)
	assigned_to: string | null
	assigned_name: string | null
	due_at: string | null
	map_id: string | null
	map_slug: string | null
	map_public_token: string | null
	document_id: string | null // actul emis (cerut la status 'issued')
	document_code: string | null // nr. de registru al actului, pentru afișare
	was_issued?: boolean // a trecut prin 'issued' — cetățeanul păstrează actul și după închidere
	feature_ref: string | null
	location: { lng: number; lat: number } | null
	location_source: string
	created_at: string
	updated_at: string
	last_message_at: string | null
	last_message_role: "citizen" | "operator" | ""
}
/** O pagină din inbox (server: `RequestPage`) — `total` = câte se potrivesc filtrelor. */
interface RequestPageData {
	items: RequestSummary[]
	total: number
	limit: number
	offset: number
}
interface RequestDetailData extends RequestSummary {
	messages: RequestMessage[]
	attachments: RequestAttachment[]
}
/** Un eveniment din ISTORICUL cererii (cine/când/din ce în ce/de ce) — oglindește RequestEvent din server. */
interface RequestEvent {
	id: string
	actor_id: string | null
	actor_role: "citizen" | "operator" | "system"
	actor_name: string
	kind: "created" | "status" | "assign" | "due" | "document"
	from_value: string | null
	to_value: string | null
	from_label: string
	to_label: string
	note: string | null
	created_at: string
}

type RequestStatus =
	| "new"
	| "assigned"
	| "in_progress"
	| "answered"
	| "issued"
	| "closed"
	| "rejected"

type RequestTipAct = "sesizare" | "cu_informare" | "aviz" | "observatie_consultare"

const STATUS_META: Record<RequestStatus, { label: string; cls: string }> = {
	new: { label: "Nouă", cls: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200" },
	assigned: {
		label: "Preluată",
		cls: "bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300",
	},
	in_progress: {
		label: "În lucru",
		cls: "bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300",
	},
	answered: {
		label: "Răspuns",
		cls: "bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300",
	},
	issued: { label: "Emisă", cls: "bg-teal-100 dark:bg-teal-950 text-teal-800 dark:text-teal-300" },
	closed: {
		label: "Închisă",
		cls: "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
	},
	rejected: { label: "Respinsă", cls: "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300" },
}
// Ordinea pentru filtrul de status din inbox (mașina de stări LEGALĂ trăiește pe server → next_statuses).
const STATUS_ORDER: RequestStatus[] = [
	"new",
	"assigned",
	"in_progress",
	"answered",
	"issued",
	"closed",
	"rejected",
]
/** Cele 4 tipuri de act (etichete RO) pentru selectul din formularul de depunere. */
const TIP_ACT_OPTIONS: Array<{ value: RequestTipAct; label: string }> = [
	{ value: "sesizare", label: "Sesizare" },
	{ value: "cu_informare", label: "Certificat de urbanism de informare" },
	{ value: "aviz", label: "Aviz" },
	{ value: "observatie_consultare", label: "Observație la consultare publică" },
]
const ROLE_LABEL: Record<string, string> = {
	citizen: "Cetățean",
	operator: "Primărie",
	system: "Sistem",
}

// ─────────────────────────────────────────────
// Utilitare
// ─────────────────────────────────────────────
/**
 * „Ultimul mesaj: cetățean · <data>" pe cardurile din liste — evidențiat când vine de la CEALALTĂ
 * parte (așteaptă răspuns). Semnal fără stare de „necitit" (decizia 04.09). Montat static: span gol
 * ascuns când nu există mesaje (regula Van — fără null din funcții de randare).
 */
function last_message_hint(
	req: { last_message_at: string | null; last_message_role: string },
	viewer: "citizen" | "operator",
) {
	if (!req.last_message_at) {
		return t.span({ class: "hidden" })
	}
	const from_other = req.last_message_role !== viewer
	const who = req.last_message_role === "citizen" ? "cetățean" : "primărie"
	return t.span(
		{ class: from_other ? "text-cyan-700 dark:text-cyan-300 font-medium" : "" },
		`Ultimul mesaj: ${who} · ${fmt_date(req.last_message_at)}`,
	)
}

function fmt_date(iso: string | null): string {
	if (!iso) {
		return "—"
	}
	const d = new Date(iso)
	return isNaN(d.getTime())
		? "—"
		: d.toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })
}
function fmt_size(n: number): string {
	if (n < 1024) {
		return `${n} B`
	}
	if (n < 1024 * 1024) {
		return `${(n / 1024).toFixed(0)} KB`
	}
	return `${(n / 1024 / 1024).toFixed(1)} MB`
}
function status_badge(status: RequestStatus) {
	const m = STATUS_META[status] ?? STATUS_META.new
	return t.span(
		{ class: `inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${m.cls}` },
		m.label,
	)
}
/** Cip cu tipul actului — eticheta vine gata din server (`tip_act_label`), UI-ul nu re-mapează coduri. */
function tip_act_chip(label: string) {
	return t.span(
		{
			class:
				"inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 text-xs font-medium",
		},
		label,
	)
}
/** Rol de PLATFORMĂ (super/support) — poate vedea orice inbox, chiar fără membership. */
function is_platform_admin(): boolean {
	const r = session.val.user?.platform_role
	return r === "super" || r === "support"
}

/** Descompune un `feature_ref` (`<layer_slug>:<uuid>[#<attr_key>]`) în componente pentru afișare + link. */
function parse_feature_ref(ref: string): { layer: string; id: string; attr: string } {
	const colon = ref.indexOf(":")
	const layer = colon >= 0 ? ref.slice(0, colon) : ""
	const rest = colon >= 0 ? ref.slice(colon + 1) : ref
	const hash = rest.indexOf("#")
	return {
		layer,
		id: hash >= 0 ? rest.slice(0, hash) : rest,
		attr: hash >= 0 ? rest.slice(hash + 1) : "",
	}
}

/**
 * Cont de LUCRU (staff) = membru al unei primării SAU rol de platformă. Direcția 1 (separarea
 * rolurilor): contul de lucru e DOAR operator — NU are latură de cetățean. Vede exclusiv inbox-ul
 * primăriei; „Solicitările mele" / „Solicitare nouă" rămân doar pentru cetățeanul pur (fără
 * membership). O solicitare personală se depune dintr-un cont de cetățean separat.
 */
function is_staff_user(): boolean {
	return session.val.memberships.length > 0 || is_platform_admin()
}

/** Operatorul (editor+ pe org-ul solicitării, sau super) vede controalele de primărie. */
function is_operator_of(org_id: string): boolean {
	const s = session.val
	if (s.user?.platform_role === "super") {
		return true
	}
	return s.memberships.some(
		(m) => m.org_id === org_id && (m.role === "owner" || m.role === "admin" || m.role === "editor"),
	)
}

// ─────────────────────────────────────────────
// Guard: cere autentificare
// ─────────────────────────────────────────────
function LoginPrompt() {
	return t.div(
		{ class: "max-w-lg mx-auto px-4 py-16 text-center" },
		t.div({ class: "text-4xl" }, "🔒"),
		t.h1(
			{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
			"Autentificare necesară",
		),
		t.p(
			{ class: "mt-2 text-slate-600 dark:text-slate-300" },
			"Pentru a depune și urmări solicitări către primărie ai nevoie de un cont.",
		),
		t.a(
			{
				// Păstrează ținta curentă (ex. ?new=1&org=&map_id=) ca să revină aici după autentificare.
				href: `#/autentificare?return=${encodeURIComponent(location.hash.slice(1) || "/solicitari")}`,
				class:
					"inline-block mt-6 px-5 py-2.5 rounded-lg bg-cyan-700 text-white font-medium hover:bg-cyan-800",
			},
			"Autentificare / cont nou",
		),
	)
}

// ─────────────────────────────────────────────
// Listă: „Solicitările mele"
// ─────────────────────────────────────────────
function MyRequestsList() {
	const rows = van.state<RequestSummary[] | null>(null)
	// Filtre CLIENT-SIDE (F5-T5): lista proprie a unui cetățean e mică — nu merită un roundtrip
	// per filtru (inbox-ul operatorului, potențial mare, filtrează server-side).
	const filter_status = van.state("")
	const filter_tip = van.state("")
	const load = async () => {
		const r = await api_get<RequestSummary[]>("/api/requests/mine")
		rows.val = r.ok ? r.data : []
	}
	void load()

	const filtered = (): RequestSummary[] =>
		(rows.val ?? []).filter(
			(x) =>
				(!filter_status.val || x.status === filter_status.val) &&
				(!filter_tip.val || x.tip_act === filter_tip.val),
		)
	const SEL_CLS =
		"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-xs outline-none bg-white dark:bg-slate-900"
	const filters_bar = t.div(
		{ class: "flex flex-wrap gap-2 mb-4" },
		t.select(
			{
				class: SEL_CLS,
				onchange: (e: Event) => (filter_status.val = (e.target as HTMLSelectElement).value),
			},
			t.option({ value: "" }, "Toate stările"),
			...STATUS_ORDER.map((s) => t.option({ value: s }, STATUS_META[s].label)),
		),
		t.select(
			{
				class: SEL_CLS,
				onchange: (e: Event) => (filter_tip.val = (e.target as HTMLSelectElement).value),
			},
			t.option({ value: "" }, "Toate tipurile"),
			...TIP_ACT_OPTIONS.map((o) => t.option({ value: o.value }, o.label)),
		),
	)

	const card = (req: RequestSummary) =>
		t.button(
			{
				class:
					"w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:border-cyan-400 " +
					"hover:shadow-sm transition cursor-pointer",
				onclick: () => navigate(`/solicitari?id=${encodeURIComponent(req.id)}`),
			},
			t.div(
				{ class: "flex items-start justify-between gap-3" },
				t.div(
					{ class: "min-w-0" },
					t.div(
						{ class: "font-semibold text-slate-800 dark:text-slate-200 truncate" },
						req.subject,
					),
					t.div(
						{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
						req.org_name || req.org_slug,
					),
				),
				status_badge(req.status),
			),
			t.div(
				{
					class:
						"mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400",
				},
				tip_act_chip(req.tip_act_label),
				t.span(`Actualizat: ${fmt_date(req.updated_at)}`),
				last_message_hint(req, "citizen"),
				req.due_at
					? t.span(
							{ class: "text-amber-700 dark:text-amber-300" },
							`Termen: ${fmt_date(req.due_at)}`,
						)
					: "",
			),
		)

	return t.div(
		{ class: "max-w-3xl mx-auto px-4 py-6" },
		t.div(
			{ class: "flex items-center justify-between mb-5" },
			TabHeader("mine"),
			t.a(
				{
					href: "#/solicitari?new=1",
					class:
						"px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800",
				},
				"+ Solicitare nouă",
			),
		),
		filters_bar,
		() => {
			if (rows.val === null) {
				return t.div(
					{ class: "text-slate-400 dark:text-slate-500 py-10 text-center animate-pulse" },
					"Se încarcă…",
				)
			}
			if (rows.val.length === 0) {
				return t.div(
					{
						class:
							"rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-10 text-center text-slate-500 dark:text-slate-400",
					},
					t.p("Nu ai depus încă nicio solicitare."),
					t.a(
						{
							href: "#/solicitari?new=1",
							class: "text-cyan-700 dark:text-cyan-300 font-medium hover:underline",
						},
						"Depune prima solicitare →",
					),
				)
			}
			const list = filtered()
			if (list.length === 0) {
				return t.div(
					{ class: "text-sm text-slate-500 dark:text-slate-400 py-8 text-center" },
					"Nicio solicitare nu se potrivește filtrelor alese.",
				)
			}
			return t.div({ class: "space-y-3" }, ...list.map(card))
		},
	)
}

// ─────────────────────────────────────────────
// Formular: „Solicitare nouă"
// ─────────────────────────────────────────────
/** Tipurile acceptate la atașamente (client-side; serverul revalidează cu allow-list). */
const ATTACH_ACCEPT = "image/*,application/pdf,.docx,.xlsx"
const ATTACH_ALLOWED_PREFIX = ["image/", "application/pdf", "application/vnd.openxmlformats"]

function NewRequestForm(preset: {
	org: string
	map: string
	feature_ref: string
	lng: string
	lat: string
	source: string
	tip_act: string
	subject: string
	category: string
}) {
	const orgs = van.state<Array<{ slug: string; name: string }>>([])
	const err = van.state("")
	const busy = van.state(false)

	void (async () => {
		const r = await api_get<Array<{ slug: string; name: string }>>("/api/requests/directory")
		orgs.val = r.ok ? r.data : []
	})()

	// Context spațial pre-completat din /embed (pin sau feature). Serverul îl revalidează.
	const lng = Number(preset.lng)
	const lat = Number(preset.lat)
	const has_pin =
		preset.lng !== "" && preset.lat !== "" && Number.isFinite(lng) && Number.isFinite(lat)

	const org_select = t.select({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
	})
	van.derive(() => {
		while (org_select.options.length > 0) {
			org_select.remove(0)
		}
		org_select.appendChild(new Option("— alege primăria —", ""))
		for (const o of orgs.val) {
			org_select.appendChild(new Option(o.name || o.slug, o.slug))
		}
		if (preset.org) {
			org_select.value = preset.org
		}
	})

	const subject_input = t.input({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
		placeholder: "Ex.: Sesizare groapă pe strada…",
		maxlength: "300",
	})
	const category_input = t.input({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
		placeholder: "sesizare / cerere / informare (opțional)",
		maxlength: "60",
	})
	// GHIȘEU (audit 17.09): funcționarul depune în numele cetățeanului, deci trebuie să spună CINE e
	// solicitantul — altfel rubrica „solicitant" a actului iese cu numele contului care depune, adică
	// al primăriei. Se arată doar conturilor de lucru; serverul le acceptă doar de la un operator al
	// primăriei alese (fail-closed) — un cetățean nu-și poate pune alt nume pe act.
	const at_counter = is_staff_user()
	const applicant_input = t.input({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
		placeholder: "Nume și prenume / denumirea firmei",
		maxlength: "200",
	})
	const applicant_address_input = t.input({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
		placeholder: "Domiciliul sau sediul solicitantului",
		maxlength: "300",
	})

	// Tipul actului solicitat (nomenclator ÎNCHIS) — determină fluxul de emitere pe partea de operator.
	const tip_act_select = t.select(
		{
			class:
				"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500",
		},
		...TIP_ACT_OPTIONS.map((o) => new Option(o.label, o.value)),
	)
	// Pre-completare din punte (panoul de regim → ?tip_act=cu_informare) — doar valori cunoscute.
	if (preset.tip_act && TIP_ACT_OPTIONS.some((o) => o.value === preset.tip_act)) {
		tip_act_select.value = preset.tip_act
	}
	// Pre-completare din bannerul de consultare (F6-T6): subiectul poartă numele consultării
	// (editabil), categoria — referința mașinal-filtrabilă `consultare:<slug>`.
	if (preset.subject) {
		subject_input.value = preset.subject
	}
	if (preset.category) {
		category_input.value = preset.category
	}
	const body_input = t.textarea({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500 min-h-[8rem]",
		placeholder: "Descrie solicitarea ta…",
		maxlength: "10000",
	})
	const file_input = t.input({
		type: "file",
		multiple: true,
		accept: ATTACH_ACCEPT,
		class:
			"block w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:rounded-lg file:border-0 " +
			"file:bg-cyan-50 file:px-3 file:py-1.5 file:text-cyan-700 file:font-semibold file:cursor-pointer",
	})

	const submit = async () => {
		err.val = ""
		const org = org_select.value
		const subject = subject_input.value.trim()
		const body = (body_input.value as string).trim()
		if (!org) {
			err.val = "Alege primăria destinatară."
			return
		}
		if (!subject || !body) {
			err.val = "Completează subiectul și descrierea."
			return
		}
		const applicant = at_counter ? applicant_input.value.trim() : ""
		const applicant_address = at_counter ? applicant_address_input.value.trim() : ""
		// Pe actele emise (CU / aviz) numele solicitantului e o rubrică obligatorie a formularului-cadru:
		// mai bine îl cerem aici decât să iasă actul pe numele primăriei (constatarea care a cerut câmpul).
		if (
			at_counter &&
			!applicant &&
			(tip_act_select.value === "cu_informare" || tip_act_select.value === "aviz")
		) {
			err.val = "Completează numele solicitantului — apare pe actul emis."
			return
		}
		// Validare atașamente pe client (mesaj prompt); serverul e sursa de adevăr (allow-list + plafoane).
		const files = Array.from((file_input as HTMLInputElement).files ?? [])
		if (files.length > 10) {
			err.val = "Maxim 10 fișiere per solicitare."
			return
		}
		for (const f of files) {
			if (f.size > 25 * 1024 * 1024) {
				err.val = `„${f.name}" depășește 25 MB.`
				return
			}
			if (!ATTACH_ALLOWED_PREFIX.some((a) => f.type.startsWith(a))) {
				err.val = `„${f.name}": tip neacceptat (imagini, PDF, Word, Excel).`
				return
			}
		}
		busy.val = true
		const r = await api_post<{ id: string }>("/api/requests", {
			org,
			subject,
			body,
			tip_act: tip_act_select.value,
			applicant_name: applicant || undefined,
			applicant_address: applicant_address || undefined,
			category: category_input.value.trim() || undefined,
			map_id: preset.map || undefined,
			feature_ref: preset.feature_ref || undefined,
			lng: has_pin ? lng : undefined,
			lat: has_pin ? lat : undefined,
			location_source: has_pin ? preset.source || "pin" : undefined,
		})
		if (!r.ok) {
			busy.val = false
			err.val = r.err?.message ?? "Trimiterea a eșuat."
			return
		}
		// Atașamente la CREARE: reutilizează endpointul existent (create JSON + chain upload).
		for (const f of files) {
			const fd = new FormData()
			fd.append("file", f)
			await api_upload(`/api/requests/${encodeURIComponent(r.data.id)}/attachments`, fd)
		}
		busy.val = false
		navigate(`/solicitari?id=${encodeURIComponent(r.data.id)}`)
	}

	const label = (s: string) =>
		t.label({ class: "block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1" }, s)

	// Notă de context (feature/pin) — statică (preset e fix).
	const context_note = preset.feature_ref
		? t.div(
				{
					class:
						"rounded-lg bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 text-cyan-800 dark:text-cyan-300 text-sm px-3 py-2",
				},
				"🔗 Solicitare despre un element selectat pe hartă.",
			)
		: has_pin
			? t.div(
					{
						class:
							"rounded-lg bg-cyan-50 dark:bg-cyan-950 border border-cyan-200 text-cyan-800 dark:text-cyan-300 text-sm px-3 py-2",
					},
					`📍 Locație selectată pe hartă: ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
				)
			: t.span({ style: "display:none" })

	return t.div(
		{ class: "max-w-2xl mx-auto px-4 py-6" },
		t.a(
			{
				href: "#/solicitari",
				class: "text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800",
			},
			"← Solicitările mele",
		),
		t.h1(
			{ class: "text-2xl font-bold text-slate-800 dark:text-slate-200 mt-2 mb-5" },
			"Solicitare nouă",
		),
		t.div(
			{
				class:
					"space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5",
			},
			context_note,
			t.div(label("Primăria"), org_select),
			t.div(label("Tip de act"), tip_act_select),
			// Montat STATIC, ascuns prin class pentru cetățean (regula Van — fără null din randare).
			t.div(
				{
					class: at_counter
						? "rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 space-y-3"
						: "hidden",
				},
				t.div(
					{ class: "text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide" },
					"Depunere la ghișeu",
				),
				t.div(label("Solicitant"), applicant_input),
				t.div(label("Domiciliul / sediul solicitantului"), applicant_address_input),
				t.p(
					{ class: "text-xs text-slate-500 dark:text-slate-400" },
					'Cetățeanul pentru care depui. Numele ajunge la rubrica „solicitant" a actului generat; ',
					"lăsat gol, actul iese pe numele contului tău (al primăriei).",
				),
			),
			t.div(label("Subiect"), subject_input),
			t.div(label("Categorie"), category_input),
			t.div(label("Descriere"), body_input),
			t.div(
				label("Atașamente (opțional)"),
				file_input,
				t.p(
					{ class: "text-xs text-slate-400 dark:text-slate-500 mt-1" },
					"Imagini, PDF, Word, Excel — max 10 fișiere, 25 MB fiecare.",
				),
			),
			() =>
				err.val
					? t.div(
							{
								class:
									"rounded-lg bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm px-3 py-2",
							},
							err.val,
						)
					: "",
			// GDPR: exact aici cetățeanul lasă nume, email, atașamente și locație → informarea și linkul
			// către politica de confidențialitate stau lângă butonul de trimitere (audit 09.09, §D).
			t.p(
				{ class: "text-xs text-slate-500 dark:text-slate-400" },
				"Prin trimitere, datele din solicitare (nume, email, atașamente, locația aleasă) ajung la primăria selectată, ",
				"care le prelucrează ca înregistrare administrativă (petiție). Primăria are un responsabil cu protecția datelor, ",
				"cu datele de contact publicate pe site-ul ei. ",
				t.a(
					{
						href: "/confidentialitate",
						target: "_blank",
						rel: "noopener",
						class: "underline hover:text-slate-800",
					},
					"Politica de confidențialitate",
				),
				".",
			),
			t.div(
				{ class: "flex items-center gap-3 pt-1" },
				t.button(
					{
						class:
							"px-5 py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 " +
							"disabled:opacity-50 cursor-pointer",
						disabled: () => busy.val,
						onclick: submit,
					},
					() => (busy.val ? "Se trimite…" : "Trimite solicitarea"),
				),
				t.a(
					{
						href: "#/solicitari",
						class: "text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800",
					},
					"Anulează",
				),
			),
		),
	)
}

// ─────────────────────────────────────────────
// Detaliu: thread + atașamente + (operator) status
// ─────────────────────────────────────────────
function RequestDetail(id: string) {
	const detail = van.state<RequestDetailData | null>(null)
	const history = van.state<RequestEvent[] | null>(null)
	const err = van.state("")
	const not_found = van.state(false)
	const busy = van.state(false) // guard anti-dublu-click pe acțiuni (mesaj/upload/preia/status)

	// Regimul urbanistic la punctul cererii (F5-T5: „detaliu cerere … regim") — DOAR pentru
	// operator: ruta internă de regim e gardată pe membri, iar regimul e context de LUCRU
	// (cetățeanul îl primește prin actul emis). Se încarcă o dată per cerere (punctul nu se
	// schimbă); doar la cererile cu locație pe o hartă (fără punct n-are ce interoga).
	const regim = van.state<Regim | null>(null)
	let regim_loaded_for = ""
	const load_regim = async (d: RequestDetailData) => {
		if (!d.map_slug || !d.location || !is_operator_of(d.org_id) || regim_loaded_for === d.id) {
			return
		}
		regim_loaded_for = d.id
		const r = await api_get<Regim>(
			`/api/maps/${encodeURIComponent(d.map_slug)}/regim?lng=${d.location.lng}&lat=${d.location.lat}`,
		)
		// Hartă fără strat UTR etc. → blocul lipsește, tăcut (nu e o eroare a cererii).
		regim.val = r.ok ? r.data : null
	}

	// Reîncarcă detaliul ȘI istoricul: orice acțiune care schimbă starea (status/asignare) apelează
	// `reload`, deci timeline-ul rămâne mereu sincron cu ce vede cetățeanul în thread.
	const reload = async () => {
		const r = await api_get<RequestDetailData>(`/api/requests/${encodeURIComponent(id)}`)
		if (r.ok) {
			detail.val = r.data
			not_found.val = false
			void load_regim(r.data)
		} else {
			not_found.val = true
			err.val = r.err?.message ?? "Solicitarea nu e disponibilă."
		}
		const h = await api_get<RequestEvent[]>(`/api/requests/${encodeURIComponent(id)}/history`)
		history.val = h.ok ? h.data : []
	}
	void reload()

	// — Mesaj nou —
	const reply_input = t.textarea({
		class:
			"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500 min-h-[5rem]",
		placeholder: "Scrie un mesaj…",
		maxlength: "10000",
	})
	const send_reply = async () => {
		const body = (reply_input.value as string).trim()
		if (!body || busy.val) {
			return
		}
		busy.val = true
		const r = await api_post(`/api/requests/${encodeURIComponent(id)}/messages`, { body })
		busy.val = false
		if (r.ok) {
			reply_input.value = ""
			await reload()
		} else {
			err.val = r.err?.message ?? "Trimiterea mesajului a eșuat."
		}
	}

	// — Atașament —
	const file_input = t.input({
		type: "file",
		class:
			"block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:rounded file:border-0 " +
			"file:bg-slate-100 file:px-3 file:py-1.5 file:text-slate-700 cursor-pointer",
		onchange: async () => {
			const f = (file_input as HTMLInputElement).files?.[0]
			if (!f || busy.val) {
				return
			}
			busy.val = true
			const fd = new FormData()
			fd.append("file", f)
			const r = await api_upload(`/api/requests/${encodeURIComponent(id)}/attachments`, fd)
			busy.val = false
			;(file_input as HTMLInputElement).value = ""
			if (r.ok) {
				await reload()
			} else {
				err.val = r.err?.message ?? "Încărcarea fișierului a eșuat."
			}
		},
	})

	// — Mesaj din thread —
	// Alinierea e pe PERSPECTIVĂ (convenția de chat: ale mele în dreapta), nu pe rol:
	// cetățeanul își vede mesajele lui în dreapta, operatorul pe ale lui.
	// `applicant` = solicitantul de la ghișeu (gol online). Mesajele cu rol „cetățean" de pe o cerere
	// de ghișeu sunt scrise de funcționar în numele lui — eticheta bulei arăta numele PRIMĂRIEI la
	// „Cetățean", ceea ce citit din thread părea că primăria își scrie singură.
	const message_row = (m: RequestMessage, viewer_is_operator: boolean, applicant: string) => {
		const mine = viewer_is_operator ? m.author_role === "operator" : m.author_role === "citizen"
		if (m.author_role === "system") {
			return t.div(
				{ class: "text-center text-xs text-slate-400 dark:text-slate-500 py-1" },
				`${m.body} · ${fmt_date(m.created_at)}`,
			)
		}
		return t.div(
			{ class: `flex ${mine ? "justify-end" : "justify-start"}` },
			t.div(
				{
					class:
						"max-w-[85%] rounded-2xl px-4 py-2.5 " +
						(mine
							? "bg-cyan-700 text-white"
							: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200"),
				},
				t.div(
					{
						class: `text-[11px] font-semibold mb-0.5 ${mine ? "text-cyan-100" : "text-slate-500 dark:text-slate-400"}`,
					},
					applicant && m.author_role === "citizen"
						? `${ROLE_LABEL.citizen} · ${applicant} (la ghișeu)`
						: `${ROLE_LABEL[m.author_role] ?? m.author_role} · ${m.author_name}`,
				),
				t.div({ class: "text-sm whitespace-pre-wrap break-words" }, m.body),
				...m.attachments.map((a) =>
					t.a(
						{
							href: `/api/requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(a.id)}`,
							class: `mt-1 block text-xs underline ${mine ? "text-cyan-100" : "text-cyan-700 dark:text-cyan-300"}`,
						},
						`📎 ${a.filename} (${fmt_size(a.size_bytes)})`,
					),
				),
				t.div(
					{
						class: `text-[10px] mt-1 ${mine ? "text-cyan-200" : "text-slate-400 dark:text-slate-500"}`,
					},
					fmt_date(m.created_at),
				),
			),
		)
	}

	// — Link de descărcare pentru un atașament —
	const attachment_link = (a: RequestAttachment) =>
		t.a(
			{
				href: `/api/requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(a.id)}`,
				class: "flex items-center gap-1.5 text-sm text-cyan-700 dark:text-cyan-300 hover:underline",
			},
			`📎 ${a.filename} (${fmt_size(a.size_bytes)})`,
		)

	// — Panou operator (preluare + mașina de stări) —
	const OperatorPanel = (d: RequestDetailData) => {
		const sel_status = van.state<string>("") // "" = nicio schimbare aleasă
		const note = van.state<string>("") // motiv (obligatoriu la respingere/redeschidere)
		const sel_doc = van.state<string>(d.document_id ?? "") // actul emis (la 'issued')
		const docs = van.state<Array<{ id: string; label: string }> | null>(null)

		// Motiv cerut: respingere SAU redeschidere (starea curentă terminală → în lucru).
		const needs_note = (): boolean => {
			const s = sel_status.val
			return (
				s === "rejected" ||
				((d.status === "closed" || d.status === "rejected") && s === "in_progress")
			)
		}
		// Act cerut: emitere.
		const needs_doc = (): boolean => sel_status.val === "issued"

		// Documentele primăriei pentru emitere — filtrate pe harta cererii dacă există (altfel toate
		// cele vizibile operatorului, scope pe org). Se încarcă doar dacă emiterea e o tranziție posibilă.
		const load_docs = async () => {
			const q = d.map_slug ? `?map=${encodeURIComponent(d.map_slug)}` : ""
			const r = await api_get<Array<{ id: string; name: string; registry_code: string }>>(
				`/api/documents${q}`,
			)
			docs.val = r.ok
				? r.data.map((doc) => ({
						id: doc.id,
						label: doc.registry_code ? `${doc.registry_code} — ${doc.name}` : doc.name || doc.id,
					}))
				: []
		}
		if (d.next_statuses.includes("issued")) {
			void load_docs()
		}

		// Select de status — DOAR next_statuses (tranzițiile legale, deja filtrate pe tip_act de server).
		const status_select = t.select(
			{
				class:
					"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none",
				onchange: (e: Event) => (sel_status.val = (e.target as HTMLSelectElement).value),
			},
			new Option("— schimbă statusul —", ""),
			...d.next_statuses.map((s) => new Option(STATUS_META[s].label, s)),
		)
		const due_input = t.input({
			type: "date",
			class:
				"rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none",
			value: d.due_at ? d.due_at.slice(0, 10) : "",
		})
		// Motiv/observație — ACCEPTAT la orice tranziție (intră în istoric), OBLIGATORIU doar la
		// respingere/redeschidere. Nod STABIL (F8: nu detașa noduri cu legături reactive).
		const note_input = t.textarea({
			class:
				"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none min-h-[3rem]",
			placeholder: () =>
				needs_note() ? "Motivul deciziei (obligatoriu)…" : "Motiv / observație (opțional)…",
			maxlength: "1000",
			oninput: (e: Event) => (note.val = (e.target as HTMLTextAreaElement).value),
		})
		// Select de document — populat reactiv din `docs`, pre-selectat pe actul deja setat.
		const doc_select = t.select({
			class:
				"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none",
			onchange: (e: Event) => (sel_doc.val = (e.target as HTMLSelectElement).value),
		})
		van.derive(() => {
			const list = docs.val
			while (doc_select.options.length > 0) {
				doc_select.remove(0)
			}
			doc_select.appendChild(new Option("— alege actul emis —", ""))
			for (const o of list ?? []) {
				doc_select.appendChild(new Option(o.label, o.id))
			}
			doc_select.value = sel_doc.val
		})

		// Confirmarea e blocată cât lipsește o intrare obligatorie (serverul oricum respinge — dar
		// userul nu trebuie să afle așa).
		const cannot_apply = (): boolean =>
			busy.val ||
			!sel_status.val ||
			(needs_note() && !note.val.trim()) ||
			(needs_doc() && !sel_doc.val)

		const apply = async () => {
			if (cannot_apply()) {
				return
			}
			busy.val = true
			const r = await api_post(`/api/requests/${encodeURIComponent(id)}/status`, {
				status: sel_status.val,
				due_at: (due_input as HTMLInputElement).value || null,
				note: note.val.trim() || undefined,
				document_id: needs_doc() ? sel_doc.val : undefined,
			})
			busy.val = false
			if (r.ok) {
				sel_status.val = ""
				note.val = ""
				;(note_input as HTMLTextAreaElement).value = ""
				await reload()
			} else {
				err.val = r.err?.message ?? "Actualizarea a eșuat."
			}
		}

		// — Generare act din șablon (F5-T5) — doar actele care se emit (cu_informare/aviz) și doar
		// pe cereri cu HARTĂ (documentul se scoped-uiește pe harta primăriei; serverul validează
		// fail-closed). `request_id` face autofill din regim + solicitant și ATAȘEAZĂ actul cererii.
		const can_gen = (d.tip_act === "cu_informare" || d.tip_act === "aviz") && !!d.map_slug
		const gen_open = van.state(false)
		const gen_tpl = van.state("")
		const gen_reg = van.state("")
		const gen_err = van.state("")
		const gen_tpls = van.state<Array<{ slug: string; name: string }> | null>(null)
		const gen_regs = van.state<Array<{ slug: string; name: string }> | null>(null)
		const load_gen_options = async () => {
			if (gen_tpls.val !== null) {
				return
			}
			const tr = await api_get<Array<{ slug: string; name: string }>>("/api/templates")
			gen_tpls.val = tr.ok ? tr.data.map((x) => ({ slug: x.slug, name: x.name })) : []
			if (!gen_tpl.val) {
				// Șablonul oficial CU (F.6) e alegerea implicită când există.
				const cu = gen_tpls.val.find((x) => x.slug === "certificat_urbanism")
				gen_tpl.val = cu?.slug ?? gen_tpls.val[0]?.slug ?? ""
			}
			const rr = await api_get<Array<{ slug: string; name: string }>>(
				`/api/maps/${encodeURIComponent(d.map_slug ?? "")}/registries`,
			)
			gen_regs.val = rr.ok ? rr.data.map((x) => ({ slug: x.slug, name: x.name })) : []
		}
		const gen_tpl_select = t.select({
			class:
				"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none",
			onchange: (e: Event) => (gen_tpl.val = (e.target as HTMLSelectElement).value),
		})
		van.derive(() => {
			const list = gen_tpls.val
			while (gen_tpl_select.options.length > 0) {
				gen_tpl_select.remove(0)
			}
			for (const o of list ?? []) {
				gen_tpl_select.appendChild(new Option(o.name || o.slug, o.slug))
			}
			gen_tpl_select.value = gen_tpl.val
		})
		const gen_reg_select = t.select({
			class:
				"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-2 py-1.5 text-sm outline-none",
			onchange: (e: Event) => (gen_reg.val = (e.target as HTMLSelectElement).value),
		})
		van.derive(() => {
			const list = gen_regs.val
			while (gen_reg_select.options.length > 0) {
				gen_reg_select.remove(0)
			}
			gen_reg_select.appendChild(new Option("— fără număr de registru —", ""))
			for (const o of list ?? []) {
				gen_reg_select.appendChild(new Option(o.name || o.slug, o.slug))
			}
			gen_reg_select.value = gen_reg.val
		})
		const generate_act = async () => {
			if (!gen_tpl.val || busy.val) {
				return
			}
			busy.val = true
			gen_err.val = ""
			// allow_missing: câmpurile fără sursă rămân goale în docx — operatorul le completează
			// la rectificare; autofill-ul (regim/solicitant/emitent) vine din request_id.
			const r = await api_post(`/api/templates/${encodeURIComponent(gen_tpl.val)}/generate`, {
				data: {},
				map: d.map_slug,
				registry: gen_reg.val || undefined,
				request_id: id,
				allow_missing: true,
			})
			busy.val = false
			if (r.ok) {
				gen_open.val = false
				await reload()
			} else {
				gen_err.val = r.err?.message ?? "Generarea a eșuat."
			}
		}
		const gen_section = !can_gen
			? ""
			: t.div(
					{
						class:
							"rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30 p-3 space-y-2",
					},
					t.button(
						{
							class:
								"text-xs font-semibold text-emerald-800 dark:text-emerald-300 hover:text-emerald-900 cursor-pointer",
							onclick: () => {
								gen_open.val = !gen_open.val
								if (gen_open.val) {
									void load_gen_options()
								}
							},
						},
						() =>
							`${gen_open.val ? "▾" : "▸"} Generează act din șablon` +
							(d.document_code
								? ` (act atașat: ${d.document_code})`
								: d.document_id
									? " (act atașat)"
									: ""),
					),
					t.div(
						{ class: () => (gen_open.val ? "space-y-2" : "hidden") },
						t.div(
							{ class: "text-[11px] text-slate-500 dark:text-slate-400" },
							"Câmpurile se completează automat din regimul parcelei (UTR/POT/CUT/Rh), solicitant și emitent; restul rămân de completat.",
						),
						gen_tpl_select,
						gen_reg_select,
						() =>
							gen_err.val
								? t.div({ class: "text-xs text-red-600 dark:text-red-400" }, gen_err.val)
								: t.div(),
						t.button(
							{
								class:
									"px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 cursor-pointer disabled:opacity-40",
								disabled: () => busy.val || !gen_tpl.val,
								onclick: generate_act,
							},
							() => (d.document_id ? "Re-generează actul" : "Generează actul"),
						),
					),
				)

		return t.div(
			{
				class:
					"rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/40 p-4 mb-4 space-y-3",
			},
			t.div(
				{ class: "text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wide" },
				"Panou primărie",
			),
			// Responsabil. Statusul spune UNDE e cererea în flux; asta spune CINE răspunde de ea.
			// Trecerea într-o stare de lucru auto-asignează (server) → butonul dispare singur.
			t.div(
				{ class: "flex flex-wrap items-center gap-2" },
				d.assigned_to
					? t.div(
							{ class: "text-xs text-slate-600 dark:text-slate-300" },
							d.assigned_to === session.val.user?.id
								? "Preluată de tine"
								: `Preluată de: ${d.assigned_name}`,
						)
					: t.div(
							{ class: "text-xs font-medium text-amber-700 dark:text-amber-300" },
							"Nepreluată",
						),
				// „Preia" cât timp nu e a mea (nepreluată SAU preluată de altcineva → preluare de la el).
				d.assigned_to === session.val.user?.id
					? ""
					: t.button(
							{
								class:
									"px-3 py-1.5 rounded-lg bg-blue-700 text-white text-xs font-semibold hover:bg-blue-800 cursor-pointer",
								onclick: async () => {
									if (busy.val) {
										return
									}
									busy.val = true
									const r = await api_post(`/api/requests/${encodeURIComponent(id)}/assign`, {})
									busy.val = false
									if (r.ok) {
										await reload()
									} else {
										err.val = r.err?.message ?? "Preluarea a eșuat."
									}
								},
							},
							d.assigned_to ? `Preia de la ${d.assigned_name}` : "Preia solicitarea",
						),
			),
			// Generarea actului (F5-T5) — înaintea mașinii de stări: fluxul e preia → generează → emite.
			gen_section,
			// Mașina de stări — doar dacă există tranziții legale (altfel: stare finală).
			d.next_statuses.length === 0
				? t.div(
						{ class: "text-xs text-slate-500 dark:text-slate-400 italic" },
						"Cererea e într-o stare finală.",
					)
				: t.div(
						{ class: "space-y-2" },
						t.div(
							{ class: "flex flex-wrap items-center gap-2" },
							status_select,
							t.label({ class: "text-xs text-slate-500 dark:text-slate-400" }, "Termen:"),
							due_input,
						),
						// Motiv — vizibil de îndată ce s-a ales o tranziție (opțional peste tot,
						// obligatoriu la respingere/redeschidere — vezi placeholder-ul reactiv).
						t.div({ class: () => (sel_status.val ? "" : "hidden") }, note_input),
						// Emitere: selector de document — ascuns până se alege „Emisă".
						t.div({ class: () => (needs_doc() ? "space-y-1" : "hidden") }, doc_select, () =>
							docs.val && docs.val.length === 0
								? t.div(
										{ class: "text-xs text-amber-700 dark:text-amber-300" },
										"Nu există documente pe această hartă. Generează întâi actul în secțiunea Documente.",
									)
								: "",
						),
						t.button(
							{
								class:
									"px-3 py-1.5 rounded-lg border border-blue-300 text-blue-800 text-xs font-semibold hover:bg-blue-100 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed",
								disabled: cannot_apply,
								onclick: apply,
							},
							"Aplică",
						),
					),
		)
	}

	// — Panou „Istoric" (timeline) — vizibil ȘI cetățeanului (transparența procesului). —
	const describe_event = (e: RequestEvent): { icon: string; text: string; motiv: string } => {
		switch (e.kind) {
			case "created":
				return { icon: "📝", text: "Cerere depusă", motiv: "" }
			case "status": {
				const from = e.from_label || e.from_value || "—"
				const to = e.to_label || e.to_value || "—"
				return { icon: "🔄", text: `Status: ${from} → ${to}`, motiv: e.note ?? "" }
			}
			case "assign":
				// La asignare, `note` poartă numele operatorului preluat (nu un motiv).
				return { icon: "👤", text: `Preluată de ${e.note || "operator"}`, motiv: "" }
			case "due":
				return {
					icon: "⏰",
					text: e.to_value
						? `Termen de răspuns: ${fmt_date(e.to_value)}`
						: "Termen de răspuns eliminat",
					motiv: e.note ?? "",
				}
			case "document":
				return { icon: "📄", text: "Act emis atașat cererii", motiv: "" }
			default:
				return { icon: "•", text: e.kind, motiv: e.note ?? "" }
		}
	}
	const event_row = (e: RequestEvent) => {
		const d = describe_event(e)
		return t.li(
			{ class: "relative pl-6" },
			t.span({ class: "absolute left-0 top-0.5" }, d.icon),
			t.div({ class: "text-sm text-slate-800 dark:text-slate-200" }, d.text),
			t.div(
				{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
				`${e.actor_name || ROLE_LABEL[e.actor_role] || "Sistem"} · ${fmt_date(e.created_at)}`,
			),
			d.motiv
				? t.div(
						{
							class:
								"text-xs text-slate-600 dark:text-slate-300 mt-1 rounded bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 px-2 py-1",
						},
						`Motiv: ${d.motiv}`,
					)
				: "",
		)
	}
	const HistoryPanel = () =>
		t.div(
			{
				class:
					"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 mb-4",
			},
			t.div(
				{
					class:
						"text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3",
				},
				"Istoric",
			),
			() => {
				const h = history.val
				if (h === null) {
					return t.div(
						{ class: "text-slate-400 dark:text-slate-500 text-sm animate-pulse" },
						"Se încarcă…",
					)
				}
				if (h.length === 0) {
					return t.div(
						{ class: "text-slate-400 dark:text-slate-500 text-sm" },
						"Fără evenimente înregistrate.",
					)
				}
				return t.ol({ class: "space-y-3" }, ...h.map(event_row))
			},
		)

	const header = (d: RequestDetailData) => {
		// Staff (membru al primăriei ticketului sau super/support) → harta INTERNĂ (editare + documente);
		// cetățean → embed-ul public read-only (dacă harta e publicată). `extra` = &feature=/&lng=&lat=.
		const staff_view =
			is_platform_admin() || session.val.memberships.some((m) => m.org_slug === d.org_slug)
		const map_href = (extra: string): string => {
			if (staff_view && d.map_slug) {
				return `${location.origin}/#/map?map=${encodeURIComponent(d.map_slug)}${extra}`
			}
			if (d.map_public_token) {
				return `/embed?k=${encodeURIComponent(d.map_public_token)}${extra}`
			}
			return "" // fără acces intern și fără link public → afișăm doar text (span)
		}
		const map_link = (href: string, label: string) =>
			href
				? t.a(
						{
							href,
							target: "_blank",
							rel: "noopener",
							class: "text-cyan-700 dark:text-cyan-300 font-medium hover:underline",
						},
						label,
					)
				: t.span({ class: "text-cyan-700 dark:text-cyan-300" }, label)
		return t.div(
			{ class: "mb-4" },
			t.a(
				{
					href: "#/solicitari",
					class: "text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800",
				},
				is_staff_user() ? "← Inbox primărie" : "← Solicitările mele",
			),
			t.div(
				{ class: "flex items-start justify-between gap-3 mt-2" },
				t.div(
					t.h1({ class: "text-xl font-bold text-slate-800 dark:text-slate-200" }, d.subject),
					t.div(
						{ class: "text-sm text-slate-500 dark:text-slate-400 mt-0.5" },
						`${d.org_name || d.org_slug} · ${d.category}`,
					),
					// GHIȘEU: solicitantul REAL — cel care apare pe act. Stă sub titlu, nu în rândul mic
					// de meta: pe cererile de ghișeu e informația după care operatorul caută cererea,
					// iar contul care a depus (numele primăriei) e peste tot în rest.
					d.applicant_name
						? t.div(
								{ class: "text-sm text-slate-700 dark:text-slate-200 mt-1" },
								t.span({ class: "font-semibold" }, "Solicitant: "),
								d.applicant_name,
								d.applicant_address
									? t.span(
											{ class: "text-slate-500 dark:text-slate-400" },
											` · ${d.applicant_address}`,
										)
									: "",
								t.span(
									{ class: "text-xs text-slate-400 dark:text-slate-500" },
									` (depusă la ghișeu de ${d.citizen_name})`,
								),
							)
						: "",
				),
				t.div(
					{ class: "flex flex-col items-end gap-1 flex-shrink-0" },
					status_badge(d.status),
					tip_act_chip(d.tip_act_label),
				),
			),
			t.div(
				{ class: "mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400" },
				t.span(`Depusă: ${fmt_date(d.created_at)}`),
				d.due_at
					? t.span(
							{ class: "text-amber-700 dark:text-amber-300" },
							`Termen de răspuns: ${fmt_date(d.due_at)}`,
						)
					: "",
				d.assigned_name ? t.span(`Operator: ${d.assigned_name}`) : "",
				d.map_slug ? map_link(map_href(""), "📍 Hartă referențiată") : "",
				d.feature_ref
					? (() => {
							const fr = parse_feature_ref(d.feature_ref)
							const label = `🔗 Element: ${fr.layer || "hartă"}${fr.attr ? ` · ${fr.attr}` : ""}`
							// Deschide harta FIX pe acel element (fly + panou de detaliu).
							return map_link(fr.id ? map_href(`&feature=${encodeURIComponent(fr.id)}`) : "", label)
						})()
					: "",
				d.location
					? map_link(
							map_href(`&lng=${d.location.lng}&lat=${d.location.lat}`),
							`📍 Locație: ${d.location.lat.toFixed(5)}, ${d.location.lng.toFixed(5)}`,
						)
					: "",
			),
		)
	}

	// — Cardul ACTULUI — proeminent, nu un link pierdut în header (feedback UI). Descărcarea
	// merge prin ruta CERERII (`/api/requests/:id/document`) — autorizată pe cerere, nu pe
	// tenant, altfel cetățeanul nu și-ar putea descărca propriul act (ruta de document e
	// gardată pe membru de org). Cetățeanul îl vede DOAR după emitere (draftul e de lucru).
	// — Regimul urbanistic la punctul cererii (F5-T5) — card compact, doar operator. —
	const regim_card = (d: RequestDetailData) => {
		if (!is_operator_of(d.org_id) || !d.location || !d.map_slug) {
			return ""
		}
		const ind_row = (label: string, v: unknown) => {
			const s = v == null ? "" : String(v).trim()
			return s
				? t.div(
						{ class: "min-w-0" },
						t.span(
							{ class: "text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400" },
							label + " ",
						),
						t.span(
							{ class: "text-xs font-semibold text-slate-800 dark:text-slate-200 break-words" },
							s,
						),
					)
				: ""
		}
		return t.div(() => {
			const r = regim.val
			if (!r) {
				return t.div() // fără strat UTR / încă neîncărcat → nimic (nu e o eroare a cererii)
			}
			return t.div(
				{
					class:
						"rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30 p-4 mb-4",
				},
				t.div(
					{ class: "flex items-center justify-between gap-2 mb-1.5" },
					t.div(
						{
							class:
								"text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wide",
						},
						"Regim urbanistic la punctul cererii",
					),
					t.a(
						{
							href: `${location.origin}/#/map?map=${encodeURIComponent(d.map_slug ?? "")}&lng=${d.location?.lng}&lat=${d.location?.lat}`,
							target: "_blank",
							rel: "noopener",
							class: "text-xs text-cyan-700 dark:text-cyan-300 hover:underline flex-shrink-0",
						},
						"deschide în hartă ↗",
					),
				),
				r.in_extravilan
					? t.div(
							{ class: "text-xs text-slate-600 dark:text-slate-300" },
							"Punctul cererii e în extravilan (în afara UTR-urilor hărții).",
						)
					: t.div(
							// Derogarea PRIMA (REQ-URB-008) — prevalează asupra regimului de bază.
							r.derogare
								? t.div(
										{
											class:
												"rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-800 px-2.5 py-1.5 mb-2 text-xs text-amber-900 dark:text-amber-200",
										},
										t.span({ class: "font-bold" }, "Derogare (PUZ/HCL): "),
										[r.derogare.act, r.derogare.hcl].filter(Boolean).join(" · ") ||
											"act fără denumire",
									)
								: "",
							t.div(
								{ class: "flex items-baseline gap-2" },
								t.span(
									{ class: "text-lg font-bold text-emerald-800 dark:text-emerald-300" },
									r.utr.cod || "?",
								),
								t.span(
									{ class: "text-xs text-slate-500 dark:text-slate-400" },
									String(r.indicatori.denumire ?? "").trim(),
								),
							),
							t.div(
								{ class: "flex flex-wrap gap-x-4 gap-y-1 mt-1.5" },
								ind_row("POT max", r.indicatori.pot_max),
								ind_row("CUT max", r.indicatori.cut_max),
								ind_row("Rh max", r.indicatori.rh_max),
							),
							r.functiuni.admise || r.functiuni.interzise
								? t.details(
										{ class: "mt-2" },
										t.summary(
											{
												class:
													"cursor-pointer text-xs font-semibold text-emerald-700 dark:text-emerald-300 select-none",
											},
											"Funcțiuni (admise / interzise)",
										),
										t.div(
											{ class: "mt-1 space-y-1 text-xs text-slate-700 dark:text-slate-200" },
											r.functiuni.admise
												? t.div(t.span({ class: "font-semibold" }, "Admise: "), r.functiuni.admise)
												: "",
											r.functiuni.admise_cu_conditionari
												? t.div(
														t.span({ class: "font-semibold" }, "Cu condiționări: "),
														r.functiuni.admise_cu_conditionari,
													)
												: "",
											r.functiuni.interzise
												? t.div(
														t.span({ class: "font-semibold" }, "Interzise: "),
														r.functiuni.interzise,
													)
												: "",
										),
									)
								: "",
						),
			)
		})
	}

	const act_card = (d: RequestDetailData) => {
		const operator_view = is_operator_of(d.org_id)
		if (!d.document_id || (!operator_view && d.status !== "issued" && !d.was_issued)) {
			return ""
		}
		const issued = d.status === "issued" || Boolean(d.was_issued)
		return t.div(
			{
				class:
					"rounded-xl border border-teal-300 dark:border-teal-800 bg-teal-50/70 dark:bg-teal-950/40 p-4 mb-4 flex items-center justify-between gap-3 flex-wrap",
			},
			t.div(
				t.div(
					{ class: "text-xs font-bold text-teal-800 dark:text-teal-300 uppercase tracking-wide" },
					issued ? "📄 Act emis" : "📄 Act pregătit (nepublicat către solicitant)",
				),
				t.div(
					{ class: "text-sm text-slate-700 dark:text-slate-200 mt-0.5" },
					d.document_code ? `Nr. registru: ${d.document_code}` : "fără număr de registru",
				),
			),
			t.a(
				{
					href: `/api/requests/${encodeURIComponent(id)}/document`,
					target: "_blank",
					rel: "noopener",
					class:
						"px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-800",
				},
				"⬇ Descarcă actul (.docx)",
			),
		)
	}

	return t.div({ class: "max-w-3xl mx-auto px-4 py-6" }, () => {
		if (not_found.val) {
			return t.div(
				{ class: "text-center py-16" },
				t.p(
					{ class: "text-slate-500 dark:text-slate-400" },
					err.val || "Solicitarea nu e disponibilă.",
				),
				t.a(
					{
						href: "#/solicitari",
						class: "text-cyan-700 dark:text-cyan-300 font-medium hover:underline",
					},
					"← Înapoi la solicitări",
				),
			)
		}
		const d = detail.val
		if (!d) {
			return t.div(
				{ class: "text-slate-400 dark:text-slate-500 py-10 text-center animate-pulse" },
				"Se încarcă…",
			)
		}
		return t.div(
			header(d),
			act_card(d),
			regim_card(d),
			is_operator_of(d.org_id) ? OperatorPanel(d) : "",
			() =>
				err.val
					? t.div(
							{
								class:
									"rounded-lg bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm px-3 py-2 mb-3",
							},
							err.val,
						)
					: "",
			t.div(
				{ class: "space-y-2.5 mb-4" },
				...d.messages.map((m) => message_row(m, is_operator_of(d.org_id), d.applicant_name)),
			),
			// Documente atașate direct solicitării (fără mesaj) — altfel ar fi invizibile.
			(() => {
				const loose = d.attachments.filter((a) => !a.message_id)
				return loose.length > 0
					? t.div(
							{
								class:
									"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 mb-4",
							},
							t.div(
								{
									class:
										"text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2",
								},
								"Documente atașate",
							),
							t.div({ class: "space-y-1.5" }, ...loose.map(attachment_link)),
						)
					: ""
			})(),
			HistoryPanel(),
			t.div(
				{
					class:
						"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 space-y-3",
				},
				reply_input,
				t.div(
					// mobil: file-input-ul + butonul se stivuiesc (rândul nu încape la 390px)
					{ class: "flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap" },
					t.div({ class: "flex-1 min-w-0" }, file_input),
					t.button(
						{
							class:
								"px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 cursor-pointer flex-shrink-0",
							onclick: send_reply,
						},
						"Trimite mesaj",
					),
				),
			),
		)
	})
}

// ─────────────────────────────────────────────
// Inbox operator (primărie): coada de solicitări adresate org-ului
// ─────────────────────────────────────────────

/**
 * Titlu simplu al listei. Direcția 1: rolurile nu se mai amestecă — cetățeanul vede doar
 * „Solicitările mele", staff-ul doar „Inbox primărie" → fără comutator de taburi.
 */
function TabHeader(active: "mine" | "inbox") {
	return t.h1(
		{ class: "text-2xl font-bold text-slate-800 dark:text-slate-200" },
		active === "inbox" ? "Inbox primărie" : "Solicitările mele",
	)
}

/** Inbox-ul primăriei: solicitările adresate org-ului (operator = membru). Filtrare pe status. */
function OperatorInbox() {
	const memb = session.val.memberships.map((m) => ({ slug: m.org_slug, name: m.org_name }))
	// Super/support n-au membership, dar pot vedea orice inbox → aleg din directorul de primării.
	const orgs = van.state<Array<{ slug: string; name: string }>>(memb)
	const sel = van.state(memb[0]?.slug ?? "")
	const status = van.state("")
	const tip_act = van.state("")
	const mine = van.state(false)
	// 4.4: „Departamentul meu" (`?department=me`) — vizibil doar când membership-ul din org-ul
	// selectat are un departament; se resetează la schimbarea primăriei (altfel ar filtra pe gol).
	const dept = van.state(false)
	const my_department = (slug: string) =>
		session.val.memberships.find((m) => m.org_slug === slug)?.department ?? ""
	const rows = van.state<RequestSummary[] | null>(null)
	// Căutare + paginare: `q` e textul APLICAT (nu ce se tastează — căutarea pleacă la Enter/buton,
	// ca la geocoder), `offset` sare la 0 la orice schimbare de filtru, altfel ai cere pagina 7
	// dintr-un rezultat cu 2 rânduri.
	const q = van.state("")
	const offset = van.state(0)
	const total = van.state(0)
	const PAGE = 50

	if (memb.length === 0 && is_platform_admin()) {
		void (async () => {
			const r = await api_get<Array<{ slug: string; name: string }>>("/api/requests/directory")
			if (r.ok && r.data.length > 0) {
				orgs.val = r.data
				if (!sel.val) {
					sel.val = r.data[0].slug
				}
			}
		})()
	}

	let seq = 0 // ordinea cererilor: doar ultimul răspuns contează (filtre schimbate rapid)
	const load = async () => {
		const my_turn = ++seq
		if (!sel.val) {
			rows.val = []
			return
		}
		rows.val = null
		const qs = new URLSearchParams()
		if (status.val) {
			qs.set("status", status.val)
		}
		if (tip_act.val) {
			qs.set("tip_act", tip_act.val)
		}
		if (mine.val) {
			qs.set("mine", "1")
		}
		if (dept.val && my_department(sel.val)) {
			qs.set("department", "me")
		}
		if (q.val) {
			qs.set("q", q.val)
		}
		qs.set("limit", String(PAGE))
		qs.set("offset", String(offset.val))
		const r = await api_get<RequestPageData>(
			`/api/orgs/${encodeURIComponent(sel.val)}/requests?${qs.toString()}`,
		)
		if (my_turn !== seq) {
			return // răspuns întârziat pentru un filtru/pagină de dinainte — se aruncă
		}
		rows.val = r.ok ? r.data.items : []
		total.val = r.ok ? r.data.total : 0
	}
	// Schimbarea unui FILTRU (sau a căutării) repune paginarea la început. Paginarea NU e o
	// dependență a acestui derive — paginile se cer explicit din butoane (`go`), altfel resetul la 0
	// și trecerea la pagina următoare s-ar declanșa una pe alta.
	van.derive(() => {
		void sel.val
		void status.val
		void tip_act.val
		void mine.val
		void dept.val
		void q.val
		offset.val = 0
		void load()
	})
	const go = (next: number) => {
		offset.val = Math.max(0, next)
		void load()
	}
	van.derive(() => {
		if (!my_department(sel.val)) {
			dept.val = false
		}
	})

	// Selector de primărie — reactiv la lista `orgs` (se populează async pt. super/support).
	const org_selector = t.select({
		class:
			"rounded-lg border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-sm outline-none",
		onchange: (e: Event) => (sel.val = (e.target as HTMLSelectElement).value),
	})
	van.derive(() => {
		const list = orgs.val
		while (org_selector.options.length > 0) {
			org_selector.remove(0)
		}
		for (const o of list) {
			org_selector.appendChild(new Option(o.name || o.slug, o.slug))
		}
		org_selector.value = sel.val
	})

	// Căutarea pleacă la Enter (sau din buton), nu la fiecare tastă: un inbox mare n-are nevoie de
	// un roundtrip per literă, iar funcționarul oricum tastează un număr sau un nume întreg.
	const search_input = t.input({
		type: "search",
		class:
			"w-56 rounded-lg border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-sm outline-none focus:border-cyan-500",
		placeholder: "Caută după nume sau subiect…",
		maxlength: "120",
		onkeydown: (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				q.val = (e.target as HTMLInputElement).value.trim()
			}
			if (e.key === "Escape") {
				;(e.target as HTMLInputElement).value = ""
				q.val = ""
			}
		},
	})
	const search_box = t.div(
		{ class: "flex items-center gap-1" },
		search_input,
		t.button(
			{
				class:
					"px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm cursor-pointer " +
					"hover:border-cyan-500",
				title: "Caută (Enter)",
				onclick: () => (q.val = (search_input as HTMLInputElement).value.trim()),
			},
			"🔍",
		),
	)

	const status_filter = t.select(
		{
			class:
				"rounded-lg border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-sm outline-none",
			onchange: (e: Event) => (status.val = (e.target as HTMLSelectElement).value),
		},
		new Option("Toate", ""),
		...STATUS_ORDER.map((s) => new Option(STATUS_META[s].label, s)),
	)
	const tip_act_filter = t.select(
		{
			class:
				"rounded-lg border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-sm outline-none",
			onchange: (e: Event) => (tip_act.val = (e.target as HTMLSelectElement).value),
		},
		new Option("Toate tipurile", ""),
		...TIP_ACT_OPTIONS.map((o) => new Option(o.label, o.value)),
	)
	const mine_toggle = t.label(
		{
			class:
				"flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none",
		},
		t.input({
			type: "checkbox",
			class: "rounded border-slate-300 dark:border-slate-600",
			onchange: (e: Event) => (mine.val = (e.target as HTMLInputElement).checked),
		}),
		"Asignate mie",
	)
	const dept_checkbox = t.input({
		type: "checkbox",
		class: "rounded border-slate-300 dark:border-slate-600",
		onchange: (e: Event) => (dept.val = (e.target as HTMLInputElement).checked),
	})
	van.derive(() => {
		dept_checkbox.checked = dept.val
	})
	const dept_toggle = t.label(
		{
			class: () =>
				"flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none" +
				(my_department(sel.val) ? "" : " hidden"),
			title: () =>
				my_department(sel.val)
					? `Solicitările asignate colegilor din departamentul „${my_department(sel.val)}"`
					: "",
		},
		dept_checkbox,
		"Departamentul meu",
	)

	const card = (req: RequestSummary) =>
		t.button(
			{
				class:
					"w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:border-cyan-400 " +
					"hover:shadow-sm transition cursor-pointer",
				onclick: () => navigate(`/solicitari?id=${encodeURIComponent(req.id)}`),
			},
			t.div(
				{ class: "flex items-start justify-between gap-3" },
				t.div(
					{ class: "min-w-0" },
					t.div(
						{ class: "font-semibold text-slate-800 dark:text-slate-200 truncate" },
						req.subject,
					),
					t.div(
						{ class: "text-xs text-slate-500 dark:text-slate-400 mt-0.5" },
						// Ghișeu: numele SOLICITANTULUI (cel de pe act), cu contul care a depus în paranteză.
						req.applicant_name
							? `${req.applicant_name} (ghișeu: ${req.citizen_name}) · ${req.category}`
							: `${req.citizen_name} · ${req.category}`,
					),
				),
				status_badge(req.status),
			),
			t.div(
				{
					class:
						"mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400",
				},
				tip_act_chip(req.tip_act_label),
				req.assigned_name
					? t.span(`Preluată de ${req.assigned_name}`)
					: t.span({ class: "text-amber-700 dark:text-amber-300 font-medium" }, "Nepreluată"),
				t.span(`Actualizat: ${fmt_date(req.updated_at)}`),
				last_message_hint(req, "operator"),
				req.due_at
					? t.span(
							{ class: "text-amber-700 dark:text-amber-300" },
							`Termen: ${fmt_date(req.due_at)}`,
						)
					: "",
			),
		)

	if (memb.length === 0 && !is_platform_admin()) {
		return t.div(
			{ class: "max-w-3xl mx-auto px-4 py-16 text-center text-slate-500 dark:text-slate-400" },
			"Nu ești membru al niciunei primării.",
		)
	}

	return t.div(
		{ class: "max-w-3xl mx-auto px-4 py-6" },
		t.div(
			{ class: "flex flex-wrap items-center justify-between gap-3 mb-5" },
			TabHeader("inbox"),
			t.div(
				{ class: "flex flex-wrap items-center gap-2" },
				search_box,
				org_selector,
				status_filter,
				tip_act_filter,
				mine_toggle,
				dept_toggle,
			),
		),
		// Rândul de paginare: „1–50 din 20.012" + salt de pagină. Ascuns când totul încape pe o pagină.
		() => {
			const n = rows.val?.length ?? 0
			if (n === 0 || total.val <= n) {
				return t.div({ class: "hidden" })
			}
			const from = offset.val + 1
			const to = offset.val + n
			const pager_btn = (label: string, disabled: boolean, next: number) =>
				t.button(
					{
						class:
							"px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-sm " +
							(disabled ? "opacity-40" : "cursor-pointer hover:border-cyan-500"),
						disabled,
						onclick: () => go(next),
					},
					label,
				)
			return t.div(
				{
					class:
						"flex items-center justify-between gap-3 mb-3 text-sm text-slate-500 dark:text-slate-400",
				},
				t.span(`${from}–${to} din ${total.val.toLocaleString("ro-RO")}`),
				t.div(
					{ class: "flex items-center gap-2" },
					pager_btn("← Mai noi", offset.val === 0, offset.val - PAGE),
					pager_btn("Mai vechi →", to >= total.val, offset.val + PAGE),
				),
			)
		},
		() => {
			if (rows.val === null) {
				return t.div(
					{ class: "text-slate-400 dark:text-slate-500 py-10 text-center animate-pulse" },
					"Se încarcă…",
				)
			}
			if (rows.val.length === 0) {
				return t.div(
					{
						class:
							"rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-10 text-center text-slate-500 dark:text-slate-400",
					},
					q.val
						? `Nicio solicitare pentru „${q.val}" pe filtrele curente.`
						: "Nicio solicitare pe acest filtru.",
				)
			}
			return t.div({ class: "space-y-3" }, ...rows.val.map(card))
		},
	)
}

// ─────────────────────────────────────────────
// Pagina (router intern pe query)
// ─────────────────────────────────────────────
export function RequestsPage(): HTMLElement {
	const params = get_hash_query_params()
	const id = params.get("id")
	const is_new = params.get("new") === "1"
	const preset = {
		org: params.get("org") ?? "",
		map: params.get("map_id") ?? "",
		feature_ref: params.get("feature_ref") ?? "",
		lng: params.get("lng") ?? "",
		lat: params.get("lat") ?? "",
		source: params.get("location_source") ?? "",
		tip_act: params.get("tip_act") ?? "", // puntea din panoul de regim (F5-T5): cu_informare
		subject: params.get("subject") ?? "", // bannerul de consultare (F6-T6): numele consultării
		category: params.get("category") ?? "", // idem: `consultare:<layer_slug>` (filtrabil)
	}

	return t.div({ class: "min-h-[calc(100dvh-3rem)] bg-slate-50 dark:bg-slate-950" }, () => {
		if (!session.val.authenticated) {
			return LoginPrompt()
		}
		if (id) {
			return RequestDetail(id)
		}
		// Direcția 1: contul de lucru (membru/platformă) e implicit operator → inbox-ul primăriei.
		// EXCEPȚIE (F5-T5, fluxul de ghișeu): `?new=1` EXPLICIT deschide formularul și pentru staff —
		// puntea din panoul de regim („Generează CU" pe hartă) creează cererea în numele
		// solicitantului de la ghișeu. Fără ?new, staff-ul nu nimerește din greșeală latura de cetățean.
		if (is_staff_user() && !is_new) {
			return OperatorInbox()
		}
		if (is_new) {
			return NewRequestForm(preset)
		}
		return MyRequestsList()
	})
}
